/**
 * RWBR — WHERE THE TWO BOOKS MEET.
 *
 * Sagliano's sponsorship system is driven entirely by Race Week die rolls. He
 * prints the weights and the codes — "Praise a sponsor +2 (RW41, INT11, INT24)"
 * — and never reproduces the charts themselves. The RACE WEEK Events Guide
 * prints the charts and never mentions sponsorship. Neither book is usable
 * alone; this file is the join, and it is the single most load-bearing piece of
 * cross-referencing in the whole project.
 *
 * VERIFIED, NOT ASSUMED. Every one of the 66 codes below was checked against
 * the transcribed chart row it points at. All 66 exist, and all 66 say what
 * Sagliano says they say — RW41 really is "Driver praises sponsor in TV
 * segment", Y15 really is "Driver makes poorly-chosen remarks about sponsor",
 * X24 really is "Wrecks car in practice". Two independently transcribed
 * documents agreeing on 66 arbitrary two-digit codes is about as strong as
 * source corroboration gets in this project. `sponsorCodes.test.ts` re-runs
 * that check against the chart JSON so a future edit to either side breaks
 * loudly.
 *
 * CODE PREFIXES (SAG/15, verbatim):
 *
 *     RW  = Race Week TV LIST Chart
 *     INT = TV Interviews
 *     X   = TV Event X
 *     Y   = TV Event Y
 *     Z   = TV Event Z
 *
 * TWO GROUPS, THREE USES. Each entry belongs to `sponsor-mention` or
 * `public-eye`, because Sagliano's end-of-season renewal splits them into two
 * separately-scored factors (SAG/17-18, factors 3 and 4). The same weights are
 * ALSO summed together as modifiers on the in-season signup roll (SAG/15-16).
 * So one table, three consumers — which is why the group is a field rather than
 * two tables that could drift apart.
 */

import type { ChartId } from '../raceWeek.ts';

export const CITE_SPONSOR_MODIFIERS = 'SAG/15-16 — modifiers to the new-sponsor 2d6, "accumulative"';
export const CITE_SPONSOR_MENTION = 'SAG/17-18 — Value of Sponsorship, factor 3: points for sponsor mention';
export const CITE_PUBLIC_EYE = 'SAG/18 — Value of Sponsorship, factor 4: points for being in the public eye';

/** Which of the two end-of-season factors an entry scores under. */
export type SponsorEffectGroup = 'sponsor-mention' | 'public-eye';

export interface SponsorEffect {
  chart: ChartId;
  code: string;
  group: SponsorEffectGroup;
  /** Signed weight, identical in both consumers. */
  weight: number;
  /** Sagliano's own label for the row. */
  label: string;
}

/** Sagliano's prefix, expanded to this project's chart ids. */
const RW: ChartId = 'race-week-tv-list';
const INT: ChartId = 'tv-interviews';
const X: ChartId = 'tv-event-x';
const Y: ChartId = 'tv-event-y';
const Z: ChartId = 'tv-event-z';

function rows(
  chart: ChartId,
  codes: readonly string[],
  group: SponsorEffectGroup,
  weight: number,
  label: string,
): SponsorEffect[] {
  return codes.map((code) => ({ chart, code, group, weight, label }));
}

/**
 * SAG/15-18, complete. Order follows the printed modifier table so the two can
 * be read side by side.
 */
export const SPONSOR_EFFECTS: readonly SponsorEffect[] = [
  // --- factor 3: sponsor mention -------------------------------------------
  ...rows(RW, ['41'], 'sponsor-mention', 2, 'Praise a sponsor'),
  ...rows(INT, ['11', '24'], 'sponsor-mention', 2, 'Praise a sponsor'),

  ...rows(RW, ['21'], 'sponsor-mention', 1, 'Mention a sponsor'),
  ...rows(INT, ['54', '41'], 'sponsor-mention', 1, 'Mention a sponsor'),
  ...rows(X, ['23', '46'], 'sponsor-mention', 1, 'Mention a sponsor'),

  ...rows(Y, ['15', '53'], 'sponsor-mention', -3, 'Negative remarks by Driver about a current sponsor'),

  // --- factor 4: being in the public eye ------------------------------------
  ...rows(RW, ['12'], 'public-eye', 1, 'Public appearance'),
  ...rows(X, ['12'], 'public-eye', 1, 'Public appearance'),

  ...rows(RW, ['51', '61'], 'public-eye', 2, 'Media (hype) event'),
  ...rows(X, ['16', '36', '51'], 'public-eye', 2, 'Media (hype) event'),
  ...rows(X, ['42'], 'public-eye', -2, 'Media (hype) event'),

  ...rows(RW, ['14', '24', '54'], 'public-eye', 1, 'Praise fans'),
  ...rows(INT, ['45', '62'], 'public-eye', 1, 'Praise fans'),

  ...rows(X, ['43'], 'public-eye', -1, 'Problems with fans'),
  ...rows(Y, ['35', '64', '65'], 'public-eye', -1, 'Problems with fans'),

  ...rows(RW, ['31', '42'], 'public-eye', -1, 'Negative Driver image: altercations with other Drivers'),
  ...rows(X, ['32'], 'public-eye', -1, 'Negative Driver image: altercations with other Drivers'),
  ...rows(Y, ['13', '31', '43', '63'], 'public-eye', -1, 'Negative Driver image: altercations with other Drivers'),
  ...rows(Z, ['26', '56'], 'public-eye', -1, 'Negative Driver image: altercations with other Drivers'),

  ...rows(X, ['15', '53', '63'], 'public-eye', -2, 'Negative Driver image: TEAM problem'),
  ...rows(Y, ['32', '46', '56', '62'], 'public-eye', -2, 'Negative Driver image: TEAM problem'),
  ...rows(Z, ['15', '16', '35', '43', '46'], 'public-eye', -2, 'Negative Driver image: TEAM problem'),

  ...rows(Y, ['12', '16', '41'], 'public-eye', -3, 'Personal Problem of Driver comes to light during Race Week'),
  ...rows(Z, ['23', '62', '63', '65'], 'public-eye', -3, 'Personal Problem of Driver comes to light during Race Week'),
];

const EFFECT_INDEX = new Map<string, SponsorEffect>(SPONSOR_EFFECTS.map((e) => [`${e.chart}:${e.code}`, e]));

/** The sponsorship effect a chart row carries, if any. */
export function sponsorEffectFor(chart: ChartId, code: string): SponsorEffect | undefined {
  return EFFECT_INDEX.get(`${chart}:${code}`);
}

// ---------------------------------------------------------------------------
// The four rows that do something no weight can express
// ---------------------------------------------------------------------------

/**
 * SAG/16 — "A die roll of RW52 provides a new associate sponsor for this race
 * only. Add one week's associate sponsorship amount to Team."
 *
 * The chart row corroborates it exactly: "New sponsor for this race, extra
 * publicity; PLACEMENT C."
 */
export const ONE_RACE_ASSOCIATE = { chart: RW, code: '52' } as const;
export const CITE_ONE_RACE_ASSOCIATE = 'SAG/16 — Special Sponsor Sign Up: RW52, one week\'s associate amount';

/**
 * SAG/19 — "The primary sponsorship is automatically renewed for next season if
 * Z54 is rolled during a Race Week."
 *
 * Chart row: "Combination of new equipment, sponsor renewal, contributes to
 * awesome race week; PLACEMENT A."
 */
export const AUTO_RENEW_PRIMARY = { chart: Z, code: '54' } as const;
export const CITE_AUTO_RENEW = 'SAG/19 — Special Sponsorship Renewal: Z54 renews the primary automatically';

/**
 * SAG/16 — the two rows that put a driver's own primary sponsorship at risk.
 * They are also worth -3 as a mention (above); the two consequences are
 * separate and both apply.
 */
export const BAD_MOUTH_CODES: readonly { chart: ChartId; code: string }[] = [
  { chart: Y, code: '15' },
  { chart: Y, code: '53' },
];

export function isBadMouth(chart: ChartId, code: string): boolean {
  return BAD_MOUTH_CODES.some((c) => c.chart === chart && c.code === code);
}

/**
 * The rows that mean a car was wrecked or damaged badly enough to cost money
 * outside the normal weekly cost.
 *
 * SAG/29 names two by way of example — "Super Rare Results 13 or TV Event X 24,
 * for example" — and then leaves the set open with "for example". Rather than
 * guess at a wider set, this carries only the Race Week side: X24, which the
 * chart confirms as "Wrecks car in practice, PLACEMENT D". The race-side
 * trigger is detected from the race ledger's own moment tags instead, which is
 * both more reliable and more complete than a code list could be.
 */
export const RACE_WEEK_DAMAGE: readonly { chart: ChartId; code: string }[] = [{ chart: X, code: '24' }];

export function isRaceWeekDamage(chart: ChartId, code: string): boolean {
  return RACE_WEEK_DAMAGE.some((c) => c.chart === chart && c.code === code);
}

/**
 * SAG/21 — the internal TV factor "Altercation or fight during Race Week". Read
 * off the same altercation rows the public-eye factor uses, so a week that
 * costs a driver sponsor points is the same week that lifts the TV rating.
 * Which is exactly Sagliano's stated intent: bad behaviour sells.
 */
export function isAltercation(chart: ChartId, code: string): boolean {
  const effect = sponsorEffectFor(chart, code);
  return effect?.label.includes('altercations with other Drivers') ?? false;
}

// ---------------------------------------------------------------------------
// Reading a driver's week
// ---------------------------------------------------------------------------

export interface WeekSponsorTally {
  /** Every effect the week's readings triggered, in order. */
  effects: SponsorEffect[];
  /** Sum of the `sponsor-mention` group — SAG/17-18 factor 3. */
  mentionTotal: number;
  /** Sum of the `public-eye` group — SAG/18 factor 4. */
  publicEyeTotal: number;
  /** Both groups summed — the modifier bundle for SAG/15-16's signup roll. */
  modifierTotal: number;
}

/**
 * Score one driver's Race Week from the chain of chart readings his TV LIST
 * result already carries.
 *
 * THE WHOLE CHAIN COUNTS, not just the final row. A week that starts on the TV
 * LIST, hands off to EVENT X and lands on an interview passed through three
 * printed rows, and Sagliano's codes name rows on all of those charts — RW21
 * hands off, so if only the terminal row scored, RW21 could never fire at all.
 */
export function tallyWeek(chain: readonly { chart: ChartId; code: string }[]): WeekSponsorTally {
  const effects: SponsorEffect[] = [];
  for (const step of chain) {
    const effect = sponsorEffectFor(step.chart, step.code);
    if (effect) effects.push(effect);
  }

  let mentionTotal = 0;
  let publicEyeTotal = 0;
  for (const e of effects) {
    if (e.group === 'sponsor-mention') mentionTotal += e.weight;
    else publicEyeTotal += e.weight;
  }

  return { effects, mentionTotal, publicEyeTotal, modifierTotal: mentionTotal + publicEyeTotal };
}

/**
 * SAG/17-18 — a factor total converted to its ±10 points.
 *
 * The two printed tables differ by one word and it matters. Factor 3 reads
 * "Positive Total +10 / Average 0 / Negative (or Zero) Total -10" — a zero
 * scores MINUS ten. Factor 4 reads "Positive +10 / Zero Total 0 / Negative -10"
 * — a zero scores nothing. Transcribed as printed rather than harmonised,
 * because the asymmetry is consistent with the sections around it: staying
 * silent about your sponsor is a failure, staying out of the news is not.
 */
export function mentionFactorPoints(total: number): number {
  return total > 0 ? 10 : -10; // zero falls into "Negative (or Zero) Total"
}

export function publicEyeFactorPoints(total: number): number {
  return total > 0 ? 10 : total < 0 ? -10 : 0;
}
